import { useCallback, useEffect } from 'react';
import { isAccountAdobeManaged } from '@frameio/graphql-client/utils/isAccountAdobeManaged';
import { useAccountPermissions } from '@frameio/permissions';
import {
  SubscriptionUpdateSource,
  UsageLimitExceeded,
} from '@frameio/plan-management/feature-gate/config';
import { ModalType } from '@frameio/plan-management/modal/components';
import { usePlanManagementModalContext } from '@frameio/plan-management/modal/hooks';
import type { AlertBannerProps } from '../types';
import { useIsOnUsagePage } from './useIsOnUsagePage';

export function useStorageLimitBanner(
  account: AlertBannerProps['account'],
  fetchPlansData: AlertBannerProps['fetchPlansData'],
  upgradedPlan: AlertBannerProps['upgradedPlan']
) {
  const accountId = account?.id;
  const canManageBilling = useAccountPermissions({
    accountId,
    check: 'canManageBilling',
  });

  const subscription = account?.subscription;
  const tier = subscription?.tier;
  const isEnterprise = tier === 'ENTERPRISE';
  const isTeam = tier === 'TEAM';

  const activeStorage = subscription?.limitedResources?.storageBytes?.used53;
  const activeStorageLimit =
    subscription?.limitedResources?.storageBytes?.limit53;

  const isOverStorageLimit =
    activeStorage != null &&
    activeStorageLimit != null &&
    activeStorage > activeStorageLimit;
  const hasUnlimitedStorage = activeStorageLimit === null;

  const ownerEmail = account?.owner?.email ?? '';
  const isManagedByAdobe = isAccountAdobeManaged(account?.managedBy);
  const delinquentGracePeriodExpiresAt =
    account?.delinquentGracePeriodExpiresAt;
  const upgradedPlanTitle = upgradedPlan?.title;

  const planManagementModalContext = usePlanManagementModalContext();

  const openUpgradeModal = useCallback(() => {
    if (!planManagementModalContext || !upgradedPlan) return;
    // Ensure cost is a number with a default value if undefined
    const planWithDefinedCost = {
      ...upgradedPlan,
      cost: upgradedPlan.cost ?? 0, // Default to 0 if cost is undefined
    };
    planManagementModalContext.setSelectedPlanWithFeature({
      ...planWithDefinedCost,
      modalType: ModalType.CHECKOUT,
      subscriptionUpdateSource: SubscriptionUpdateSource.OVER_LIMIT_BANNER,
      usageLimitExceeded: UsageLimitExceeded.STORAGE,
    });
  }, [upgradedPlan, planManagementModalContext]);

  const isOnUsagePage = useIsOnUsagePage();
  const isAccountLocked = !!account?.lockedAt;

  const showBanner =
    Boolean(account) &&
    !hasUnlimitedStorage &&
    isOverStorageLimit &&
    !isOnUsagePage &&
    !isAccountLocked;

  useEffect(() => {
    if (showBanner) {
      fetchPlansData?.();
    }
  }, [fetchPlansData, showBanner]);

  const graceDate = delinquentGracePeriodExpiresAt
    ? new Date(delinquentGracePeriodExpiresAt)
    : null;

  return {
    canManageBilling,
    showBanner,
    subscription,
    tier,
    isEnterprise,
    isTeam,
    activeStorage,
    activeStorageLimit,
    ownerEmail,
    isManagedByAdobe,
    graceDate,
    upgradedPlanTitle,
    openUpgradeModal,
  };
}
